/**
 * Shared pure math for all map overlay implementations.
 *
 * No DOM, no map API, no shared mutable state.
 * Used by both overlays.js (image overlays) and svgOverlays.js (SVG layer overlays).
 *
 * @module mapLibrary/overlayCore
 */

/**
 * Computes the absolute pixel bounds for positioning an overlay within the map div,
 * given the projected south-west and north-east pixel corners and an optional rotation.
 *
 */
export function computePixelBounds(swPixel, nePixel, rotation) {
  return {
    left:      Math.round(Math.min(swPixel.x, nePixel.x)),
    top:       Math.round(Math.min(swPixel.y, nePixel.y)),
    width:     Math.round(Math.abs(nePixel.x - swPixel.x)),
    height:    Math.round(Math.abs(swPixel.y - nePixel.y)),
    transform: rotation ? `rotate(${rotation}deg)` : '',
  };
}

/**
 * Returns true when current pixel bounds differ from the previously stored value.
 * Used by draw() to short-circuit redundant DOM style updates.
 *
 */
export function hasPixelRectChanged(previous, current) {
  if (!previous) {
    return true;
  }
  return !(
    previous.left      === current.left   &&
    previous.top       === current.top    &&
    previous.width     === current.width  &&
    previous.height    === current.height &&
    previous.transform === current.transform
  );
}

/**
 * Pixel distance a pointer must travel between mousedown and click
 * for the event to be treated as a drag and suppressed.
 */
export const DRAG_THRESHOLD_PX = 4;

/**
 * Returns true when the pointer has moved beyond the drag threshold since mousedown.
 *
 */
export function isDragGesture(pointerDownPos, currentX, currentY) {
  if (!pointerDownPos) {
    return false;
  }
  const dx = currentX - pointerDownPos.x;
  const dy = currentY - pointerDownPos.y;
  return (dx * dx) + (dy * dy) > DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX;
}
